import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import StatusPill, { type PillVariant } from '../components/StatusPill';
import { useActiveCompany } from '../state';
import { CATEGORY_EXPLAINER, CATEGORY_META } from '../data';
import { AREA_META, AREA_ORDER, SECTION_META, sectionsIn } from '../requirementTaxonomy';
import { applyOverride, canDevOverride, hasOverrides, rescore } from '../devStatus';
import { Brand, brandify } from '../../components/Brand';
import { Alert, Chevron, Cross, Download, Tick, Upload } from '../components/AppIcons';
import ExportModal from '../components/ExportModal';
import { createDocument, uploadDocumentFile } from '../../lib/uploads';
import {
  confirmChecklistAttestation,
  CATEGORY_TO_KEBAB,
  type ChecklistCategory,
  type ChecklistItem,
  type ChecklistStatus,
  type RequirementArea,
  type RequirementSection,
} from '../../lib/entities/checklist';
import '../../styles/app-documents.css';

/** API base, mirroring apiClient: empty in dev (Vite proxies /api), set in prod. */
const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, '') ?? '';

interface AddDocumentModalProps {
  /** Modal heading, e.g. "Upload your sickness policy" or "Replace this file". */
  title: string;
  /** Company the document belongs to (needed to create a new one). */
  companyId: string;
  /**
   * Existing document to upload the file into. When omitted, a new document is
   * created from the entered name and the file uploaded to it.
   */
  documentId?: string;
  /**
   * Catalog slug of the checklist slot this upload satisfies. Passed through to
   * createDocument so a new document is coupled to the missing checklist item.
   */
  catalogId?: string;
  /** Prefilled document name for the new-document case. */
  defaultName?: string;
  onClose: () => void;
  /** Called after a file is accepted, so the caller can refetch its data. */
  onUploaded: () => void;
}

/**
 * Real upload modal, shared with the detail page's "Replace file" action. Runs
 * the document-coupled flow from lib/uploads: (create metadata →) ticket → PUT →
 * complete. A `rejected` verdict is surfaced inline rather than thrown. When a
 * `catalogId` is supplied the created document fills that checklist slot.
 */
export function AddDocumentModal({
  title,
  companyId,
  documentId,
  catalogId,
  defaultName,
  onClose,
  onUploaded,
}: AddDocumentModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState(defaultName ?? '');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pickFile = (f: File | null) => {
    setFile(f);
    setError(null);
    // Default a new document's display name to the file's base name.
    if (f && !documentId && !name.trim()) {
      setName(f.name.replace(/\.[^.]+$/, ''));
    }
  };

  const displayName = name.trim() || file?.name || 'your document';
  const canSubmit = Boolean(file) && (documentId ? true : name.trim().length > 0) && !busy;
  // PDFs go through a conversion step whose on-screen rendering isn't perfect yet, so flag it at pick time
  // and keep the upload from being a surprise. The stored file, and any download from it, keeps the text.
  const isPdfPick = !!file && (/\.pdf$/i.test(file.name) || file.type.includes('pdf'));
  // Replace vs. add-new: an existing documentId means we're swapping the file on a document already on the
  // checklist ("Replace file"); otherwise this upload creates the document ("Upload"). Never "Add to
  // checklist" — that reads wrong for a replace and is jargon for a new one.
  const submitLabel = documentId ? 'Replace file' : 'Upload';

  const submit = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      let docId = documentId;
      if (!docId) {
        const created = await createDocument(API_BASE, {
          companyId,
          name: name.trim(),
          catalogId,
        });
        docId = created.id;
      }
      const result = await uploadDocumentFile(API_BASE, docId, file);
      if (result.status === 'rejected') {
        setError(result.rejectionReason ?? 'That file couldn’t be accepted. Try a PDF or Word file.');
        setBusy(false);
        return;
      }
      onUploaded();
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
          <Cross size={18} />
        </button>

        {done ? (
          <div className="docs-success">
            <span className="docs-success__mark">
              <Tick size={24} />
            </span>
            <p className="docs-success__text">
              Got it. <strong>{displayName}</strong> is on your requirements.
            </p>
            <div className="docs-success__foot">
              <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <h2 className="modal__title">{title}</h2>
            <p className="docs-modal__sub">
              PDF, Word, and most document formats are fine. <Brand /> reads the text and checks it
              against what the law expects of a business like yours.
            </p>

            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.doc,.docx,.odt,.html,.htm,.md"
              style={{ display: 'none' }}
              onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
            />
            <button
              type="button"
              className={`docs-drop${file ? ' is-chosen' : ''}`}
              onClick={() => inputRef.current?.click()}
            >
              <span className="docs-drop__icon">
                <Upload size={20} />
              </span>
              {file ? (
                <strong>{file.name}</strong>
              ) : (
                <span>
                  <strong>Choose a file</strong> to upload
                </span>
              )}
              <span className="docs-drop__hint">
                {file
                  ? 'Ready to add, or choose a different file.'
                  : 'PDF, Word, or another text document.'}
              </span>
            </button>

            {isPdfPick && (
              <p className="docs-modal__warn" role="note">
                <Alert size={15} />
                <span>
                  PDF uploads aren’t perfect yet, so this document may display slightly wrong on
                  screen. Downloading it should give you the correct text.
                </span>
              </p>
            )}

            {error && (
              <p style={{ margin: '4px 0 0', color: 'var(--wine)', fontSize: '13.5px' }} role="alert">
                {error}
              </p>
            )}

            <div className="docs-modal__foot">
              <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={!canSubmit}
                onClick={submit}
              >
                {busy ? 'Uploading…' : submitLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Pill variant + label per resolved checklist status, for the states health doesn't refine. */
const CHECKLIST_PILL: Record<ChecklistStatus, { variant: PillVariant; label: string }> = {
  present: { variant: 'ok', label: 'In place' },
  missing: { variant: 'rose', label: 'Missing' },
  shouldNotHave: { variant: 'danger', label: 'Should not have' },
  inert: { variant: 'muted', label: 'Not needed' },
  // A company obligation should never be needsAnswer (onboarding gates the company predicates), but the
  // status is possible, so it is handled rather than crashing.
  needsAnswer: { variant: 'warn', label: 'Needs an answer' },
};

/**
 * What the pill says, once health is taken into account.
 *
 * A held document is not automatically fine. It can have findings against it, or it can still be
 * being read — and "In place" would be a claim we haven't earned in either case. Saying so in WORDS
 * also means the palette below is never the only thing carrying the difference.
 */
function pillFor(item: ChecklistItem): {
  variant: PillVariant;
  label: ReactNode;
  /** Suppress the pill's leading dot where something else already occupies that slot. */
  dot?: boolean;
} {
  if (item.status !== 'present') return CHECKLIST_PILL[item.status];
  if (item.health === 'hasFindings') {
    return {
      variant: 'warn',
      label: item.findingCount === 1 ? '1 thing to fix' : `${item.findingCount} things to fix`,
    };
  }
  if (item.health === 'unchecked') {
    // Named for what is HAPPENING, not for what is absent, and the spinner says it again in motion:
    // "we haven't got to this yet" and "this is fine" must never be confusable at a glance. The
    // spinner takes the dot's place rather than sitting next to it — two leading marks read as a
    // rendering accident.
    return {
      variant: 'teal',
      dot: false,
      label: (
        <>
          <span className="docs-spin" aria-hidden="true" />
          Checking…
        </>
      ),
    };
  }
  return CHECKLIST_PILL.present;
}

/**
 * Short counter labels. CATEGORY_META's names ("Legally required") are section headings and run long
 * under a number, and the counter's meaning is "how many are in place", which the fraction already
 * says.
 */
const TIER_COUNTER_LABEL: Record<ChecklistCategory, string> = {
  legallyRequired: 'Legally required',
  essential: 'Essential',
  bestPractice: 'Best practice',
  optional: 'Optional',
  notRelevant: 'Not relevant',
  shouldNotHave: 'Should not have',
};

/** Tier suffix used by the tone classes: how much this obligation matters. */
const TIER_TONE: Record<ChecklistCategory, string> = {
  legallyRequired: 'req',
  essential: 'ess',
  bestPractice: 'bp',
  optional: 'opt',
  notRelevant: 'bp',
  shouldNotHave: 'req',
};

/**
 * Card tone: state first, then how much the obligation matters.
 *
 * The point of shading by tier is that the old palette painted a missing best-practice policy in the
 * same rose as a missing legal requirement, so a page of small gaps looked like a page of emergencies.
 * Depth now means consequence, and a wall of pale rose reads as what it is.
 */
function toneFor(item: ChecklistItem): string {
  if (item.status === 'shouldNotHave') return 'bad';
  if (item.status === 'inert') return 'na';
  if (item.status === 'needsAnswer' || !item.category) return 'unknown';

  const tier = TIER_TONE[item.category];
  if (item.status === 'missing') return `miss-${tier}`;

  // Present. Findings and unassessed both outrank the tier, because the next action they imply is a
  // different one: edit this, versus wait for it to be read.
  if (item.health === 'hasFindings') return `find-${item.topSeverity ?? 'important'}`;
  if (item.health === 'unchecked') return 'unchecked';
  return `ok-${tier}`;
}

const isPresent = (item: ChecklistItem): boolean => item.status === 'present';

/**
 * Whether a "should not have" row is a real problem — i.e. the company actually HOLDS the thing.
 *
 * The backend resolves category F to status shouldNotHave whether or not anything is held
 * (ComplianceResolver.StatusFor ignores `has` for F), because the tier is a property of the type, not of
 * the company. So the row exists for every business, and without this check the page shows an alarming
 * card for a document nobody has — and counts it as needing attention. `has` is the difference between
 * "you must not have this" (advice, and true of everyone) and "you have this" (a live problem).
 */
const holdsProblem = (item: ChecklistItem): boolean =>
  item.status === 'shouldNotHave' && item.has;

/**
 * The items a business should act on: a gap, something unlawful on file, or a document held with
 * findings against it.
 *
 * Findings count because the work is real even though the slot is filled — a contract with an
 * unenforceable clause is not "done". Unheld should-not-haves do NOT count, which is most of them.
 */
const needsAttention = (item: ChecklistItem): boolean =>
  item.status === 'missing' ||
  holdsProblem(item) ||
  (item.status === 'present' && item.health === 'hasFindings');

/**
 * Order inside a section. This is still the checklist in levels — the level just travels on the card
 * instead of being the heading. "Should not have" leads because it is a live problem rather than a
 * gap: something is on file that shouldn't be.
 */
const LEVEL_RANK: Record<ChecklistCategory, number> = {
  shouldNotHave: 0,
  legallyRequired: 1,
  essential: 2,
  bestPractice: 3,
  optional: 4,
  notRelevant: 5,
};

/** Items whose tier can't be resolved sort first: they need an answer before anything else means much. */
const byLevel = (a: ChecklistItem, b: ChecklistItem): number =>
  (a.category ? LEVEL_RANK[a.category] : -1) - (b.category ? LEVEL_RANK[b.category] : -1);

/**
 * A single checklist row: a document type resolved to this company's status. The whole card
 * links to the document page (routed by the catalog slug) — even when nothing is on file yet —
 * where the immediate action (upload / fix / download) lives.
 */
function ChecklistCard({
  item,
  onUpload,
  onDownload,
}: {
  item: ChecklistItem;
  /** Open the upload modal for a missing document. Omitted where uploading makes no sense. */
  onUpload?: (item: ChecklistItem) => void;
  /** Open the download picker for a held file. */
  onDownload?: (item: ChecklistItem) => void;
}) {
  const pill = pillFor(item);
  // The tag says what tier this sits at, which is only worth saying when it adds something. Under the
  // "Not relevant" and "Optional" headings it repeats the heading; on a should-not-have card it repeats
  // the pill word for word.
  const REDUNDANT_TIERS: ChecklistCategory[] = ['notRelevant', 'optional', 'shouldNotHave'];
  const level =
    item.category && !REDUNDANT_TIERS.includes(item.category)
      ? CATEGORY_TO_KEBAB[item.category]
      : null;

  // The one thing most likely to be done next, on the card itself. The card still opens the document
  // page for everything else; this just saves the round trip for the action that is nearly always the
  // point of the visit. "Should not have" deliberately has none — remove versus replace is a judgement,
  // not a click.
  const action = (() => {
    if (item.status === 'missing' && onUpload) {
      // Filled only where the gap actually costs something. A filled button on all forty-three cards
      // would shout equally loudly on every one, which is the exact flattening the tier shading exists
      // to undo — the action stays available everywhere, its weight tracks the consequence.
      const loud =
        item.category === 'legallyRequired' || item.category === 'essential';
      return (
        <button
          type="button"
          className={`docs-qbtn${loud ? ' docs-qbtn--fix' : ''}`}
          onClick={() => onUpload(item)}
        >
          <Upload size={14} />
          Upload
        </button>
      );
    }
    if (item.status === 'present' && item.health === 'hasFindings') {
      return (
        <Link to={`/app/documents/${item.id}`} className="docs-qbtn">
          <Alert size={14} />
          Fix
        </Link>
      );
    }
    // Clean or still being checked: either way the file is on file and downloadable.
    if (item.status === 'present' && item.documentId && item.fileName && onDownload) {
      return (
        <button type="button" className="docs-qbtn" onClick={() => onDownload(item)}>
          <Download size={14} />
          Download
        </button>
      );
    }
    return null;
  })();

  return (
    <div className={`docs-card docs-card--link docs-card--${toneFor(item)}`}>
      {/* The name is the link, and its ::after overlay stretches the hit area over the whole card, so
          the quick action can sit above it on its own layer without nesting a button inside a link. */}
      <Link to={`/app/documents/${item.id}`} className="docs-card__name">
        {item.name}
      </Link>
      <div className="docs-card__pill">
        <StatusPill variant={pill.variant} dot={pill.dot ?? true}>
          {pill.label}
        </StatusPill>
        {level && (
          <span
            className={`docs-card__level${level === 'legally-required' ? ' docs-card__level--req' : ''}`}
          >
            {CATEGORY_META[level].label}
          </span>
        )}
      </div>
      {action && <div className="docs-card__actions">{action}</div>}
      <span className="docs-card__go" aria-hidden="true">
        <Chevron size={16} />
      </span>
    </div>
  );
}

/**
 * The items that don't apply to this business. They are usually the largest band on the page and
 * acting on them is never the job, so they sit behind a disclosure: still auditable, no longer the
 * bulk of the scroll.
 */
function NotRelevantDrawer({ items }: { items: ChecklistItem[] }) {
  const [open, setOpen] = useState(false);
  if (items.length === 0) return null;

  return (
    <section className="docs-quiet">
      <button
        type="button"
        className="docs-quiet__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="docs-quiet__chev" aria-hidden="true">
          <Chevron size={16} />
        </span>
        Not relevant to you ({items.length})
      </button>
      <p className="docs-quiet__note">
        Fine to skip for a business like yours. <Brand /> will say if that changes.
      </p>
      {open && (
        <div className="docs-quiet__body docs-grid">
          {items.map((item) => (
            <ChecklistCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * The tier explanation, folded away behind its own question.
 *
 * <p>The body is generic to the tier, so it is identical on every check at that level. Open by
 * default it dominated a modal whose actual job is a yes/no about one specific obligation; as a
 * question you can unfold, it stays available for the first time someone wonders what "essential"
 * means and out of the way every time after.</p>
 */
function TierDisclosure({ explainer }: { explainer: { title: string; body: string } }) {
  const [open, setOpen] = useState(false);

  return (
    <section className={`acard acard--pad docs-tier docs-tierfold${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="docs-tierfold__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="docs-tierfold__chev" aria-hidden="true">
          <Chevron size={14} />
        </span>
        {explainer.title}
      </button>
      {open && <p className="docs-card-body docs-tierfold__body">{brandify(explainer.body)}</p>}
    </section>
  );
}

/**
 * One self-certified check, opened from its pill.
 *
 * <p>Attestations are the things reeve cannot read off a file — the poster on the staff-room wall, the
 * declaration filed with a regulator. They are all-or-nothing: there is no document to assess, so no
 * findings and no checking state, and they score the full tier weight when confirmed and nothing when
 * not. Confirming one is a legal statement about the business, so the modal shows what is actually
 * being claimed and what the tier means before offering the button.</p>
 */
function AttestationModal({
  item,
  busy,
  onConfirm,
  onDeny,
  onClose,
}: {
  item: ChecklistItem;
  busy: boolean;
  onConfirm: () => void;
  onDeny: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const done = isPresent(item);
  const tier = item.category ? CATEGORY_TO_KEBAB[item.category] : null;
  const explainer = tier ? CATEGORY_EXPLAINER[tier] : null;

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={item.name}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
          <Cross size={18} />
        </button>

        <div className="docs-assert-modal__tier">
          {tier && (
            <span
              className={`docs-card__level${tier === 'legally-required' ? ' docs-card__level--req' : ''}`}
            >
              {CATEGORY_META[tier].label}
            </span>
          )}
          {done && (
            <StatusPill variant="ok">
              <Tick size={12} />
              Confirmed
            </StatusPill>
          )}
        </div>

        <h2 className="modal__title">{item.name}</h2>

        {item.why && <p className="docs-card-body">{brandify(item.why)}</p>}

        {/* Why it is on THIS company's list, kept apart from what it is. Quiet, because it answers a
            question the user is less likely to be asking at the moment they confirm. */}
        {item.appliesWhen && (
          <p className="docs-assert-modal__applies">
            <strong>Applies to you because:</strong> {item.appliesWhen}
          </p>
        )}

        {/* Collapsed by default. It is the same paragraph on every check at that tier, so once you
            have read it once it is only in the way of the thing you actually came to decide. */}
        {explainer && <TierDisclosure explainer={explainer} />}

        <div className="docs-modal__foot">
          {/* Both answers are offered explicitly. A single "confirm" button with no way back makes the
              honest answer the one with no button, which is how checklists end up ticked. */}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={busy}
            onClick={onDeny}
          >
            {done ? 'Not done after all' : 'Not done yet'}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy || done}
            onClick={onConfirm}
          >
            <Tick size={15} />
            {busy ? 'Saving…' : done ? 'Confirmed' : 'Yes, this is done'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The self-certified checks, as a row of pills above the tabs.
 *
 * <p>They used to sit at the bottom of each tab as a full list, which is where a short list of legally
 * required statements is easiest to scroll past. There are only ever a handful, so they fit across the
 * top, colour-coded like the cards: done is green, outstanding takes its tier's red. The detail that a
 * list was spending vertical space on moves into a modal, which is also where a legal statement ought
 * to be confirmed rather than from a button in a row.</p>
 */
function AttestationStrip({
  attestations,
  companyId,
  onChanged,
}: {
  attestations: ChecklistItem[];
  companyId: string;
  onChanged: () => void | Promise<void>;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [pending, setPending] = useState<Set<string>>(() => new Set());

  const setBusy = (id: string, busy: boolean) =>
    setPending((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });

  const change = async (item: ChecklistItem, confirmed: boolean) => {
    if (pending.has(item.id)) return; // one in-flight request per check
    setBusy(item.id, true);
    try {
      await confirmChecklistAttestation(companyId, item.id, confirmed);
      await onChanged();
      setOpenId(null);
    } catch {
      // Leave it as it was; the next action or reload reconciles.
    } finally {
      setBusy(item.id, false);
    }
  };

  // An attestation that doesn't apply to this business is not a check to confirm, and showing it with
  // a Confirm button asked the user to certify something they don't need.
  const applicable = attestations.filter((a) => a.status !== 'inert');
  if (applicable.length === 0) return null;

  // Most of these recur annually or less, so once confirmed they are noise on a page about the work
  // still outstanding. Outstanding ones are the strip; the confirmed ones fold away behind a count.
  const open = applicable.filter(needsAttention);
  const done = applicable.filter((a) => !needsAttention(a));
  const openItem = applicable.find((a) => a.id === openId) ?? null;

  const pill = (a: ChecklistItem) => {
    const isDone = isPresent(a);
    const tier = a.category ? CATEGORY_TO_KEBAB[a.category] : null;
    return (
      <button
        key={a.id}
        type="button"
        className={`docs-apill docs-apill--${isDone ? 'done' : (tier ?? 'best-practice')}`}
        onClick={() => setOpenId(a.id)}
      >
        {isDone ? <Tick size={13} /> : <span className="docs-apill__dot" aria-hidden="true" />}
        {a.name}
      </button>
    );
  };

  return (
    <section className="docs-asserts">
      <div className="docs-asserts__head">
        <h2 className="docs-asserts__title">Checks you confirm yourself</h2>
        <span className="docs-group__count">
          {open.length === 0 ? 'All confirmed' : `${open.length} to confirm`}
        </span>
      </div>
      {open.length > 0 && (
        <p className="docs-asserts__sub">
          <Brand /> reads your documents, but it can’t see your walls. Tap any of these to confirm it.
        </p>
      )}

      {open.length > 0 && <div className="docs-asserts__pills">{open.map(pill)}</div>}

      {done.length > 0 && (
        <div className={`docs-asserts__done${showDone ? ' is-open' : ''}`}>
          <button
            type="button"
            className="docs-asserts__toggle"
            aria-expanded={showDone}
            onClick={() => setShowDone((v) => !v)}
          >
            <span className="docs-asserts__chev" aria-hidden="true">
              <Chevron size={14} />
            </span>
            {done.length} already confirmed
          </button>
          {showDone && (
            <div className="docs-asserts__pills docs-asserts__pills--done">{done.map(pill)}</div>
          )}
        </div>
      )}

      {openItem && (
        <AttestationModal
          item={openItem}
          busy={pending.has(openItem.id)}
          onConfirm={() => void change(openItem, true)}
          onDeny={() => void change(openItem, false)}
          onClose={() => setOpenId(null)}
        />
      )}
    </section>
  );
}

export default function DocumentsPage() {
  const { companyId, companyLoading, checklist, refetchChecklist } = useActiveCompany();
  const [attentionOnly, setAttentionOnly] = useState(false);

  // The open tab lives in the URL, not in component state, so that opening a document and pressing
  // Back returns you to the tab you were on. As local state it reset to People every time, which
  // silently threw away where you were in a list of eighty things.
  //
  // Tab switches REPLACE rather than push: the tab is part of where you are, not a step in how you got
  // there, so Back should leave the page rather than walk you through the tabs you tried.
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  // People leads: it is the tab where the cost is uncapped and the other side is a person.
  const area: RequirementArea = AREA_ORDER.includes(requested as RequirementArea)
    ? (requested as RequirementArea)
    : 'employment';
  const setArea = (next: RequirementArea) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  };

  // Quick actions open their modal here rather than on the card, so only one can ever be open and the
  // card stays a presentational component.
  const [uploadFor, setUploadFor] = useState<ChecklistItem | null>(null);
  const [downloadFor, setDownloadFor] = useState<ChecklistItem | null>(null);

  // Dev status overrides live in sessionStorage, which fires no event in the tab that wrote it, so
  // devStatus dispatches its own. Without this, changing a state on a document page would not show on
  // the list until a reload.
  const [, bumpOverrides] = useState(0);
  useEffect(() => {
    const onChange = () => bumpOverrides((n) => n + 1);
    window.addEventListener('reeve-dev-override', onChange);
    return () => window.removeEventListener('reeve-dev-override', onChange);
  }, []);

  if (!companyId) {
    return (
      <div className="apage">
        <PageHeader
          eyebrow="Requirements"
          title="Documents and Policies"
          lead="Do you have everything your business needs?"
        />
        <div className="acard">
          <div className="empty">
            {companyLoading ? 'Loading…' : 'Add a company to see its requirements.'}
          </div>
        </div>
      </div>
    );
  }

  const header = (
    <PageHeader
      eyebrow="Requirements"
      title="Documents and Policies"
      lead="Do you have everything your business needs?"
    />
  );

  // First load (nothing to show yet) or a hard error: mirror the placeholders.
  if (!checklist) {
    return (
      <div className="apage">
        {header}
        <div className="acard">
          <div className="empty">
            {companyLoading
              ? 'Loading your requirements…'
              : 'We couldn’t load your requirements. Refresh to try again.'}
          </div>
        </div>
      </div>
    );
  }

  const { attestations } = checklist;
  // Dev-only display override (devStatus.ts): lets the requirement colours be reviewed without
  // contriving real uploads and assessments. Identity in production.
  const overriding = canDevOverride() && hasOverrides();
  const documents = canDevOverride()
    ? checklist.documents.map(applyOverride)
    : checklist.documents;

  // The score comes from the backend, so it is blind to the display override — which would leave the
  // one number the dev tool exists to evaluate as the only thing that never moves. Re-scored locally
  // while overrides are on, and labelled on screen so the number is never mistaken for the real one.
  const summary = overriding
    ? { ...checklist.summary, ...rescore([...documents, ...attestations]) }
    : checklist.summary;

  // Every headline counts documents AND attestations, because the page shows both and `summary` is
  // computed over both.
  const everything = [...documents, ...attestations];
  const attentionCount = everything.filter(needsAttention).length;

  // The weighted score, computed on the backend so this page and the dashboard cannot disagree.
  const score = summary.score;

  // ---- Tab split -------------------------------------------------------
  // Everything below is scoped to the open tab. Counts on the tabs themselves are computed over the
  // FULL list, so switching is an informed choice rather than a guess.
  const attentionIn = (a: RequirementArea) =>
    documents.filter((d) => d.area === a && needsAttention(d)).length +
    attestations.filter((a2) => a2.area === a && needsAttention(a2)).length;

  // Drop every "should not have" the company does NOT hold, before anything else sees it. Those rows
  // are advice that applies to everyone ("don't put an unlawful clause in a contract") and there is
  // nothing to do about one you have not got, so showing it is pure alarm with no action attached.
  const inArea = documents.filter(
    (d) => d.area === area && (d.status !== 'shouldNotHave' || d.has),
  );

  // The ones they DO hold lead the page: this is a live problem, not a gap to get round to.
  const problems = inArea.filter(holdsProblem);

  // Not relevant goes behind a disclosure; needsAnswer is pulled to the top of the tab, because an
  // unresolved tier is the one thing the page can't reason about (COMPLIANCE-MODEL.md §6) and the
  // previous grouping dropped those rows entirely.
  const unresolved = inArea.filter((d) => d.category === null);
  const notRelevant = inArea.filter((d) => d.category === 'notRelevant');
  // Optional is real but never urgent, so it leaves the working sections and collects at the bottom.
  // That takes a chunk out of every section without hiding anything.
  const optional = inArea
    .filter((d) => d.category === 'optional')
    .filter((d) => !attentionOnly || needsAttention(d))
    .sort(byLevel);

  const actionable = inArea.filter(
    (d) =>
      d.category !== null &&
      d.category !== 'notRelevant' &&
      d.category !== 'optional' &&
      !holdsProblem(d), // led the page already; don't repeat it inside a section
  );
  const visibleDocs = attentionOnly ? actionable.filter(needsAttention) : actionable;

  // Present/total per section, computed over the unfiltered list so the counts stay honest when
  // "needs attention only" is on.
  const bySection = {} as Record<RequirementSection, { present: number; total: number }>;
  for (const s of sectionsIn(area)) bySection[s] = { present: 0, total: 0 };
  for (const d of actionable) {
    bySection[d.section].total += 1;
    if (isPresent(d)) bySection[d.section].present += 1;
  }

  const sections = sectionsIn(area)
    .map((section) => ({
      section,
      items: visibleDocs.filter((d) => d.section === section).sort(byLevel),
    }))
    .filter((s) => s.items.length > 0);

  return (
    <div className="apage">
      {header}

      <div className="acard acard--pad docs-summary">
        <div className="docs-progress">
          <div className="docs-progress__headline">
            <span className="docs-progress__big">{score.percent}%</span>
            <span className="docs-progress__of">compliant</span>
            {overriding && <span className="docs-progress__dev">dev override</span>}
          </div>
          <div
            className="docs-bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={score.percent}
            aria-label="Weighted compliance score"
          >
            <div className="docs-bar__fill" style={{ width: `${score.percent}%` }} />
          </div>
          <p className="docs-progress__note">
            {/* What to do next, not how the number is calculated. The counters below already show
                where the work sits. */}
            {attentionCount === 0 ? (
              <>Everything you need is in place.</>
            ) : (
              <>
                {attentionCount} item{attentionCount === 1 ? '' : 's'} need
                {attentionCount === 1 ? 's' : ''} attention. Start with the legally required ones.
              </>
            )}
          </p>
          <div className="docs-progress__meta">
            <button
              type="button"
              className={`docs-attn${attentionOnly ? ' is-active' : ''}`}
              aria-pressed={attentionOnly}
              onClick={() => setAttentionOnly((v) => !v)}
            >
              Needs attention only
              <span className="docs-attn__count">{attentionCount}</span>
            </button>
            {/* The score counts held-but-unread documents in full, so say how much of it is
                provisional rather than letting the number imply more certainty than there is. */}
            {score.unchecked > 0 && (
              <span className="docs-provisional">
                <span className="docs-spin" aria-hidden="true" />
                {score.unchecked} still being checked
              </span>
            )}
          </div>
        </div>

        <div className="docs-stats">
          {summary.tiers
            // Optional scores nothing, so it earns no counter: showing it would imply it counts.
            .filter((t) => t.tier !== 'optional' && t.needed > 0)
            .map((t) => {
              const kebab = CATEGORY_TO_KEBAB[t.tier];
              const short = TIER_COUNTER_LABEL[t.tier];
              const behind = t.needed - t.present;
              return (
                <div className="docs-stat" key={t.tier}>
                  <div
                    className={`docs-stat__num${
                      behind > 0 && kebab === 'legally-required' ? ' docs-stat__num--rose' : ''
                    }`}
                  >
                    {t.present}/{t.needed}
                  </div>
                  <div className="docs-stat__label">{short}</div>
                  {t.withFindings > 0 && (
                    <div className="docs-stat__sub">{t.withFindings} with things to fix</div>
                  )}
                </div>
              );
            })}
          {summary.shouldNotHave > 0 && (
            <div className="docs-stat">
              <div className="docs-stat__num docs-stat__num--rose">{summary.shouldNotHave}</div>
              <div className="docs-stat__label">Should not have</div>
            </div>
          )}
        </div>
      </div>

      {/* Above the tabs, and NOT split by tab: there are only a handful, and a short list of legally
          required statements tucked under whichever tab happens to be open is a list that gets
          missed. */}
      <AttestationStrip
        attestations={attestations}
        companyId={companyId}
        onChanged={refetchChecklist}
      />

      <div className="docs-tabs" role="tablist" aria-label="Requirement type">
        {AREA_ORDER.map((a) => {
          const open = a === area;
          const count = attentionIn(a);
          return (
            <button
              key={a}
              type="button"
              role="tab"
              aria-selected={open}
              className={`docs-tab${open ? ' is-active' : ''}`}
              onClick={() => setArea(a)}
            >
              {AREA_META[a].label}
              <span className={`docs-tab__count${count === 0 ? ' docs-tab__count--clear' : ''}`}>
                {count === 0 ? <Tick size={11} /> : count}
              </span>
            </button>
          );
        })}
      </div>
      <p className="docs-tab-intro">{AREA_META[area].lead}</p>

      {/* Above everything, including the unresolved band. A gap is work outstanding; this is something
          unlawful or unenforceable already in the business, and it is the one thing on this page that
          is worse the longer it sits there. */}
      {problems.length > 0 && (
        <section className="docs-group docs-alarm">
          <div className="docs-group__head">
            <h2 className="docs-group__title docs-alarm__title">
              <Alert size={18} />
              Remove or replace {problems.length === 1 ? 'this' : 'these'}
            </h2>
            <span className="docs-group__count">
              {problems.length} on file that shouldn’t be
            </span>
          </div>
          <p className="docs-group__sub">
            {problems.length === 1 ? (
              <>
                You hold a document that the law treats as unlawful, unenforceable or misleading.
                Relying on it can be worse than having nothing, so deal with it first.
              </>
            ) : (
              <>
                You hold documents that the law treats as unlawful, unenforceable or misleading.
                Relying on them can be worse than having nothing, so deal with them first.
              </>
            )}
          </p>
          <div className="docs-grid">
            {problems.map((item) => (
              <ChecklistCard
                key={item.id}
                item={item}
                onUpload={setUploadFor}
                onDownload={setDownloadFor}
              />
            ))}
          </div>
        </section>
      )}

      {unresolved.length > 0 && (
        <section className="docs-group">
          <div className="docs-group__head">
            <h2 className="docs-group__title">Waiting on an answer</h2>
            <span className="docs-group__count">{unresolved.length} to resolve</span>
          </div>
          <p className="docs-group__sub">
            <Brand /> can’t tell what level these sit at until a question about your business is
            answered. Nothing here is assumed either way.
          </p>
          <div className="docs-grid">
            {unresolved.map((item) => (
              <ChecklistCard
                key={item.id}
                item={item}
                onUpload={setUploadFor}
                onDownload={setDownloadFor}
              />
            ))}
          </div>
        </section>
      )}

      {sections.length === 0 ? (
        <div className="docs-group">
          <div className="acard">
            <div className="empty">{AREA_META[area].empty}</div>
          </div>
        </div>
      ) : (
        sections.map(({ section, items }) => (
          <section key={section} className="docs-group">
            <div className="docs-group__head">
              <h2 className="docs-group__title">{SECTION_META[section].label}</h2>
              <span className="docs-group__count">
                {bySection[section].present} of {bySection[section].total} in place
              </span>
            </div>
            <p className="docs-group__sub">{brandify(SECTION_META[section].blurb)}</p>
            <div className="docs-grid">
              {items.map((item) => (
                <ChecklistCard
                  key={item.id}
                  item={item}
                  onUpload={setUploadFor}
                  onDownload={setDownloadFor}
                />
              ))}
            </div>
          </section>
        ))
      )}

      {optional.length > 0 && (
        <section className="docs-group">
          <div className="docs-group__head">
            <h2 className="docs-group__title">Optional</h2>
            <span className="docs-group__count">
              {optional.filter(isPresent).length} of {optional.length} in place
            </span>
          </div>
          <p className="docs-group__sub">
            Worth having as you grow, and nothing here is required. They sit at the bottom so they
            stay out of the way of the work that matters.
          </p>
          <div className="docs-grid">
            {optional.map((item) => (
              <ChecklistCard
                key={item.id}
                item={item}
                onUpload={setUploadFor}
                onDownload={setDownloadFor}
              />
            ))}
          </div>
        </section>
      )}

      <NotRelevantDrawer items={notRelevant} />

      {uploadFor && (
        <AddDocumentModal
          // Verbatim, not lowercased: catalog names carry proper nouns and parentheticals
          // ("Appropriate Policy Document (special-category & criminal-offence data)") that
          // lowercasing mangles.
          title={`Upload your ${uploadFor.name}`}
          companyId={companyId}
          catalogId={uploadFor.id}
          defaultName={uploadFor.name}
          onClose={() => setUploadFor(null)}
          onUploaded={refetchChecklist}
        />
      )}

      {downloadFor?.documentId && downloadFor.fileName && (
        <ExportModal
          instance={{
            documentId: downloadFor.documentId,
            fileName: downloadFor.fileName,
            contentType:
              downloadFor.contentType ??
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          }}
          onClose={() => setDownloadFor(null)}
        />
      )}
    </div>
  );
}
